import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
	Gift,
	Qrcode,
	DeviceMobile,
	DeviceLaptop,
} from "tabler-icons-react";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { Check, Bell, CalendarCheck } from "lucide-react";

const primaryColor = "#43bd98";

const cardVariants = {
	hidden: { opacity: 0, y: 20 },
	visible: (i) => ({
		opacity: 1,
		y: 0,
		transition: { delay: i * 0.15, duration: 0.5, ease: "easeOut" },
	}),
};

const Features = () => {
	const { t } = useTranslation();
	const isMobile = useMediaQuery("(max-width: 550px)");

	// 4 features principales - priorizando carta, fidelización, comunicación
	const mainFeatures = [
		{
			icon: <Qrcode size={28} strokeWidth={1.8} />,
			title: t("restaurantFeaturesNew.menu.title"),
			text: t("restaurantFeaturesNew.menu.text"),
			highlight: t("restaurantFeaturesNew.menu.highlight"),
		},
		{
			icon: <Gift size={28} strokeWidth={1.8} />,
			title: t("restaurantFeaturesNew.loyalty.title"),
			text: t("restaurantFeaturesNew.loyalty.text"),
			highlight: t("restaurantFeaturesNew.loyalty.highlight"),
		},
		{
			icon: <Bell size={28} strokeWidth={1.8} />,
			title: t("restaurantFeaturesNew.notifications.title"),
			text: t("restaurantFeaturesNew.notifications.text"),
			highlight: t("restaurantFeaturesNew.notifications.highlight"),
		},
		{
			icon: <CalendarCheck size={28} strokeWidth={1.8} />,
			title: t("restaurantFeaturesNew.orders.title"),
			text: t("restaurantFeaturesNew.orders.text"),
			highlight: t("restaurantFeaturesNew.orders.highlight"),
		},
	];

	// La web va primero: es lo que el restaurante da por hecho que necesita,
	// y la app entra después como el diferencial
	const platformCards = [
		{
			icon: <DeviceLaptop size={28} strokeWidth={1.8} color="#fff" />,
			title: t("restaurantFeaturesNew.web.title"),
			text: t("restaurantFeaturesNew.web.text"),
			list: t("restaurantFeaturesNew.web.list", { returnObjects: true }),
		},
		{
			icon: <DeviceMobile size={28} strokeWidth={1.8} color="#fff" />,
			title: t("restaurantFeaturesNew.app.title"),
			text: t("restaurantFeaturesNew.app.text"),
			list: t("restaurantFeaturesNew.app.list", { returnObjects: true }),
		},
	];

	return (
		<section
			style={{
				padding: "5rem 2rem",
				background: "#ffffff",
				fontFamily: "'Poppins', sans-serif",
				scrollMarginTop: "100px",
			}}
			id="funcionalidades"
		>
			<div style={{ maxWidth: "1100px", margin: "0 auto" }}>
				{/* Header */}
				<motion.div
					initial={{ opacity: 0, y: 30 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.7 }}
					viewport={{ once: true }}
					style={{ textAlign: "center", marginBottom: "3rem" }}
				>
					<p
						style={{
							color: primaryColor,
							fontSize: "0.9rem",
							textTransform: "uppercase",
							letterSpacing: "1px",
							fontWeight: 600,
							marginBottom: "0.5rem",
						}}
					>
						{t("restaurantFeaturesNew.badge")}
					</p>

					<h2
						style={{
							fontSize: "clamp(1.8rem, 5vw, 2.5rem)",
							fontWeight: 800,
							color: "#1a1a1a",
							lineHeight: 1.3,
							margin: 0,
						}}
					>
						{t("restaurantFeaturesNew.title")}
					</h2>

					<p
						style={{
						fontSize: "1.05rem",
						color: "#6b7280",
						marginTop: "1rem",
						maxWidth: "600px",
						marginInline: "auto",
						}}
					>
						{t("restaurantFeaturesNew.subtitle")}
					</p>
				</motion.div>

				{/* App + Web Cards */}
				<div
					style={{
						display: "grid",
						gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
						gap: "1.5rem",
						marginBottom: "4rem",
					}}
				>
					{platformCards.map((card, index) => (
						<motion.div
							key={card.title}
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: index * 0.1 }}
							viewport={{ once: true }}
							style={{
								background: "#f8fefc",
								border: "1px solid #e1f3ec",
								borderRadius: "20px",
								padding: isMobile ? "1.5rem" : "2rem",
								position: "relative",
								overflow: "hidden",
							}}
						>
							<div
								style={{
									position: "absolute",
									top: "-30px",
									right: "-30px",
									width: "120px",
									height: "120px",
									background: `${primaryColor}10`,
									borderRadius: "50%",
								}}
							/>

							<div
								style={{
									width: "56px",
									height: "56px",
									borderRadius: "14px",
									background: `linear-gradient(135deg, ${primaryColor} 0%, #2fae83 100%)`,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									marginBottom: "1.25rem",
									boxShadow: "0 8px 20px rgba(67,189,152,0.25)",
								}}
							>
								{card.icon}
							</div>

							<h3
								style={{
									fontSize: "1.2rem",
									fontWeight: 700,
									color: "#1a1a1a",
									marginBottom: "0.75rem",
								}}
							>
								{card.title}
							</h3>

							<p
								style={{
									color: "#4b5563",
									lineHeight: 1.6,
									fontSize: "0.95rem",
									marginBottom: "1.25rem",
								}}
							>
								{card.text}
							</p>

							<ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
								{card.list.map((item, i) => (
									<li
										key={i}
										style={{
											display: "flex",
											alignItems: "flex-start",
											gap: "10px",
											marginBottom: "10px",
											fontSize: "0.9rem",
											color: "#374151",
										}}
									>
										<Check
											size={18}
											color={primaryColor}
											strokeWidth={2.5}
											style={{ flexShrink: 0, marginTop: "2px" }}
										/>
										{item}
									</li>
								))}
							</ul>
						</motion.div>
					))}
				</div>

				{/* 4 Features Grid */}
				<motion.div
					initial={{ opacity: 0, y: 30 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6 }}
					viewport={{ once: true }}
					style={{ textAlign: "center", marginBottom: "2rem" }}
				>
					<h3
						style={{
						fontSize: "1.35rem",
						fontWeight: 700,
						color: "#1a1a1a",
						}}
					>
						{t("restaurantFeaturesNew.pillarsTitle")}
					</h3>
				</motion.div>

				<div
					style={{
						display: "grid",
						gridTemplateColumns: isMobile
							? "1fr"
							: "repeat(auto-fit, minmax(240px, 1fr))",
						gap: "1.25rem",
					}}
				>
					{mainFeatures.map((feature, i) => (
						<motion.div
							key={i}
							variants={cardVariants}
							initial="hidden"
							whileInView="visible"
							custom={i}
							viewport={{ once: true }}
							whileHover={{
								y: -4,
								boxShadow: "0 12px 30px rgba(67,189,152,0.15)",
								borderColor: primaryColor,
							}}
							style={{
								background: "#fff",
								border: "1px solid #e5e7eb",
								borderRadius: "16px",
								padding: "1.5rem",
								textAlign: "center",
								cursor: "default",
								transition: "all 0.3s ease",
							}}
						>
							<div
								style={{
									width: "52px",
									height: "52px",
									borderRadius: "12px",
									background: `${primaryColor}15`,
									color: primaryColor,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									margin: "0 auto 1rem",
								}}
							>
								{feature.icon}
							</div>

							<h4
								style={{
									fontSize: "1.05rem",
									fontWeight: 700,
									color: "#1f2937",
									marginBottom: "0.5rem",
								}}
							>
								{feature.title}
							</h4>

							<p
								style={{
									fontSize: "0.9rem",
									color: "#6b7280",
									lineHeight: 1.5,
									marginBottom: "0.75rem",
								}}
							>
								{feature.text}
							</p>

						<span
							style={{
								display: "inline-block",
								fontSize: "0.8rem",
								fontWeight: 600,
								color: primaryColor,
								background: `${primaryColor}10`,
								padding: "4px 12px",
								borderRadius: "20px",
							}}
						>
							{feature.highlight}
						</span>

					</motion.div>
					))}
				</div>
			</div>
		</section>
	);
};

export default Features;
